"use client";

import { useSnapshot } from "valtio";
import { rootStore } from "@store/rootStore";
import { useOspApiClientProvider } from "@hooks/useOspApiClientProvider";
import { useDailyMarketReadState } from "@hooks/dailyMarket/useDailyMarketReadState";
import { isDailyMarketUnread } from "@hooks/dailyMarket/isDailyMarketUnread";
import { readDailyMarketLastReadDate } from "@hooks/dailyMarket/dailyMarketReadStorage";
import { isReportMockEnabled } from "./reportDataAdapter";
import type { ReportAdapterDeps } from "./reportDataAdapter";

/** 本地自然日 yyyyMMdd（账本已读判定口径，与 dailyMarketReadStorage 写入格式一致） */
function localTodayYmd(): string {
  const d = new Date();
  return `${d.getFullYear()}${`${d.getMonth() + 1}`.padStart(2, "0")}${`${d.getDate()}`.padStart(2, "0")}`;
}

/**
 * 现成接口拼装期运行时依赖单点（adapter 方法 deps 参数的唯一来源）：
 * trackerApi（OspApiClientContext，hook 之外不可得）/ ownerId（rootStore）/
 * market 未读判定 + 日期 + markRead（useDailyMarketReadState，hook）。
 *
 * 未就绪（未登录 / api client 未建）→ null：消费方 query 以 enabled 挂起。
 */
export function useReportAdapterDeps(): ReportAdapterDeps | null {
  const { trackerApi } = useOspApiClientProvider();
  const { ownerInfo } = useSnapshot(rootStore.user);
  const ownerId = ownerInfo?.id ?? "";
  const {
    isUnread,
    reportDate,
    lastReadDate,
    isSummaryError,
    isSummaryPending,
    markRead,
  } = useDailyMarketReadState();

  if (!trackerApi || !ownerId) {
    return null;
  }

  // 账本口径的「当日 market 未读」（不依赖接口成功——接口失败时 reportDate=null、isUnread 恒 false）
  const marketUnreadByLedger = isDailyMarketUnread(
    localTodayYmd(),
    lastReadDate,
  );
  // market 日期门控（S1.7：3s 内未返回或返回失败 → 错误态+重试，而非组空直达结束页）：
  // - pending：正文请求进行中（含重试）——组挂起等待，避免新用户仅 market 未读时组空误渲染结束卡
  // - failed：正文请求失败且账本仍未读——抛错走页内错误态；账本已读则失败无关紧要（S1.12 本就不进组）
  const marketGate = isSummaryPending
    ? marketUnreadByLedger
      ? "pending"
      : "ready"
    : isSummaryError && marketUnreadByLedger
      ? "failed"
      : "ready";

  // mock 自测（2026-08-29）：本 hook 在 ReportMockApiScope Provider 之外执行，
  // market 判定走的是真实 aiApi（测试环境无当日数据 → 恒不入组）。mock 开启时
  // 直接本地判定（报告日期=今天、已读=同事 localStorage 账本），markRead 不变。
  if (isReportMockEnabled()) {
    const lastRead = readDailyMarketLastReadDate(ownerId);
    return {
      trackerApi,
      ownerId,
      marketUnread: lastRead !== localTodayYmd(),
      reportDate: localTodayYmd(),
      marketGate: "ready",
      markDailySummaryRead: markRead,
    };
  }

  return {
    trackerApi,
    ownerId,
    marketUnread: isUnread,
    reportDate,
    marketGate,
    markDailySummaryRead: markRead,
  };
}
